import React, { useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { LANDING } from '@/constants/testIds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SCHEDULE_URL = process.env.REACT_APP_SCHEDULE_URL || 'https://calendar.app.google/PwuNuRDjP7Kk1kGZA';
const API = `${BACKEND_URL}/api`;

const LATAM = [
    'Argentina', 'Bolivia', 'Brasil', 'Chile', 'Colombia', 'Costa Rica', 'Cuba',
    'Ecuador', 'El Salvador', 'Guatemala', 'Honduras', 'México', 'Nicaragua',
    'Panamá', 'Paraguay', 'Perú', 'Puerto Rico', 'Rep. Dominicana', 'Uruguay', 'Venezuela'
];

const objectives = [
    { key: 'Escalar', label: 'Escalar Mercados', testId: LANDING.formSelectObjectiveEscalar },
    { key: 'Innovacion', label: 'Innovación & Tech', testId: LANDING.formSelectObjectiveInnovacion },
    { key: 'Aprender', label: 'Aprender de Referentes', testId: LANDING.formSelectObjectiveAprender },
    { key: 'Red', label: 'Fortalecer la Red', testId: LANDING.formSelectObjectiveRed },
];

const inputCls = 'w-full bg-transparent border-b border-white/25 focus:border-[color:var(--brand)] outline-none py-3 text-white text-base md:text-lg placeholder:text-white/35 transition-colors';

const Field = ({ label, required, children }) => (
    <label className="block">
        <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-white/60 mb-2 block">
            {label}
            {required && <span className="text-[color:var(--brand)] ml-1">*</span>}
        </span>
        {children}
    </label>
);

const LeadForm = () => {
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [form, setForm] = useState({
        full_name: '',
        email: '',
        whatsapp: '',
        company: '',
        country: '',
        objective: '',
    });

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const valid = () =>
        form.full_name.trim().length > 1 &&
        /\S+@\S+\.\S+/.test(form.email) &&
        form.whatsapp.trim().length > 5 &&
        form.company.trim().length > 1 &&
        form.country.length > 0 &&
        form.objective.length > 0;

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!valid()) {
            toast.error('Completa los campos marcados para aplicar.');
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/leads`, { ...form, source: 'landing-quick' });
            setDone(true);
            toast.success('¡Aplicación recibida!');
        } catch (e) {
            const msg = e?.response?.data?.detail || 'No pudimos registrar tu aplicación. Intenta de nuevo.';
            toast.error(typeof msg === 'string' ? msg : 'Error al enviar.');
        } finally {
            setSubmitting(false);
        }
    };

    const scheduleLink = SCHEDULE_URL;
    const waLink = `https://wa.me/573158475169?text=${encodeURIComponent(`Hola, soy ${form.full_name} de ${form.company} (${form.country}). Acabo de aplicar a la Misión FITUR 2027.`)}`;

    return (
        <section id="sesion" data-testid={LANDING.formSection} className="relative py-14 md:py-18 border-t border-white/10 overflow-hidden">
            <div className="mx-auto max-w-[1200px] px-6 md:px-10">
                <div className="grid grid-cols-12 gap-5 md:gap-8 mb-10">
                    <div className="col-span-12 md:col-span-7">
                        <div className="eyebrow-brand mb-4">Aplica a la misión</div>
                        <h2 className="font-display text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
                            Dos pasos.<br />
                            <span className="italic-accent text-[color:var(--brand)] font-normal">Aplica y agenda.</span>
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-5 md:pt-2">
                        <ol className="space-y-3 text-white/75">
                            <li className="flex items-start gap-3">
                                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[color:var(--brand)] text-white text-xs font-bold">1</span>
                                <span>Completas el formulario corto de abajo (2 minutos).</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-white/25 text-white/70 text-xs font-bold">2</span>
                                <span>Recibes el link para elegir tu bloque de 30 min · te llega el Meet a tu correo.</span>
                            </li>
                        </ol>
                    </div>
                </div>

                <div className="relative border border-white/12 bg-[color:var(--surface)] p-6 md:p-10">
                    <AnimatePresence mode="wait">
                        {done ? (
                            <motion.div
                                key="done"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className="py-4"
                                data-testid={LANDING.formSuccess}
                            >
                                <div className="mb-8">
                                    <div className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand)]/15 border border-[color:var(--brand)]/40 px-3 py-1 mb-6">
                                        <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand)]" />
                                        <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-[color:var(--brand)]">Aplicación recibida</span>
                                    </div>
                                    <h3 className="font-display text-white text-4xl md:text-5xl mb-4 leading-[0.95]">
                                        Ahora agenda<br />
                                        <span className="italic-accent text-[color:var(--brand)] font-normal">tu sesión.</span>
                                    </h3>
                                    <p className="text-white/75 text-base md:text-lg leading-relaxed max-w-[70ch]">
                                        Elige abajo un bloque de 30 minutos en el calendario de Mariale.
                                        Te llegará la invitación con el link de Google Meet al correo{' '}
                                        <strong className="text-white">{form.email}</strong>.
                                    </p>
                                </div>

                                <a
                                    data-testid={LANDING.formSuccessSchedule}
                                    href={scheduleLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group flex items-center justify-between gap-6 rounded-sm border border-[color:var(--brand)]/50 bg-[color:var(--brand)]/10 px-6 py-6 md:px-8 transition-colors duration-300 hover:bg-[color:var(--brand)]/20"
                                >
                                    <span>
                                        <span className="block text-[11px] uppercase tracking-[0.2em] text-[color:var(--brand)] font-bold mb-1.5">
                                            Google Calendar · Mariale
                                        </span>
                                        <span className="block text-white text-lg md:text-xl font-semibold leading-snug">
                                            Elige tu bloque de 30 minutos
                                        </span>
                                    </span>
                                    <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[color:var(--brand)] transition-transform duration-300 group-hover:scale-110">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                            <path d="M5 12h14M13 5l7 7-7 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                </a>

                                <div className="mt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                    <div className="flex flex-wrap items-center gap-4">
                                        <a
                                            data-testid={LANDING.formSuccessWhatsapp}
                                            href={waLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="link-underline text-sm text-white/70 hover:text-white"
                                        >
                                            ¿Prefieres escribirnos? WhatsApp directo →
                                        </a>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setDone(false);
                                            setForm({ full_name: '', email: '', whatsapp: '', company: '', country: '', objective: '', attended_fitur: '' });
                                        }}
                                        className="text-[11px] uppercase tracking-[0.2em] text-white/40 hover:text-white"
                                    >
                                        Enviar otra aplicación
                                    </button>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.form
                                key="form"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onSubmit={submit}
                                className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"
                                data-testid={LANDING.formStep}
                            >
                                <Field label="Nombre completo" required>
                                    <input
                                        data-testid={LANDING.formInputName}
                                        type="text"
                                        value={form.full_name}
                                        onChange={(e) => update('full_name', e.target.value)}
                                        placeholder="Tu nombre"
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="Correo corporativo" required>
                                    <input
                                        data-testid={LANDING.formInputEmail}
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => update('email', e.target.value)}
                                        placeholder="tu@empresa.com"
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="WhatsApp (con código de país)" required>
                                    <input
                                        data-testid={LANDING.formInputWhatsapp}
                                        type="tel"
                                        value={form.whatsapp}
                                        onChange={(e) => update('whatsapp', e.target.value)}
                                        placeholder="+57 300 000 0000"
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="Empresa" required>
                                    <input
                                        data-testid={LANDING.formInputCompany}
                                        type="text"
                                        value={form.company}
                                        onChange={(e) => update('company', e.target.value)}
                                        placeholder="Nombre de tu empresa"
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="País" required>
                                    <select
                                        data-testid={LANDING.formInputCountry}
                                        value={form.country}
                                        onChange={(e) => update('country', e.target.value)}
                                        className={`${inputCls} appearance-none cursor-pointer`}
                                        style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23ffffff\' stroke-width=\'1.4\'><path d=\'M6 9l6 6 6-6\'/></svg>")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right center', backgroundSize: '20px' }}
                                    >
                                        <option value="" className="bg-[color:var(--surface)]">Selecciona tu país</option>
                                        {LATAM.map((c) => (
                                            <option key={c} value={c} className="bg-[color:var(--surface)]">{c}</option>
                                        ))}
                                    </select>
                                </Field>
                                <div className="md:col-span-2">
                                    <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-white/60 mb-3 block">
                                        Tu objetivo principal <span className="text-[color:var(--brand)] ml-1">*</span>
                                    </span>
                                    <div className="flex flex-wrap gap-2">
                                        {objectives.map((o) => (
                                            <button
                                                key={o.key}
                                                data-testid={o.testId}
                                                type="button"
                                                onClick={() => update('objective', o.key)}
                                                className={`px-4 py-2.5 rounded-full border text-sm font-medium transition-all ${
                                                    form.objective === o.key
                                                        ? 'border-[color:var(--brand)] bg-[color:var(--brand)] text-white'
                                                        : 'border-white/20 text-white/75 hover:border-white hover:text-white'
                                                }`}
                                            >
                                                {o.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                    <div>
                                        <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-white/60 mb-3 block">
                                            ¿Has participado antes en FITUR? <span className="text-[color:var(--brand)] ml-1">*</span>
                                        </span>
                                        <div className="flex gap-2">
                                            {['si', 'no'].map((v) => (
                                                <button
                                                    key={v}
                                                    data-testid={`form-attended-${v}`}
                                                    type="button"
                                                    onClick={() => update('attended_fitur', v)}
                                                    className={`px-6 py-2.5 rounded-full border text-sm font-medium uppercase tracking-wider transition-all ${
                                                        form.attended_fitur === v
                                                            ? 'border-[color:var(--brand)] bg-[color:var(--brand)] text-white'
                                                            : 'border-white/20 text-white/75 hover:border-white hover:text-white'
                                                    }`}
                                                >
                                                    {v === 'si' ? 'Sí' : 'No'}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-white/10 mt-2">
                                    <p className="text-xs text-white/45 leading-relaxed max-w-[38ch]">
                                        Sin costo, sin compromiso. Evaluamos si aplicas y te enviamos el link para agendar.
                                    </p>
                                    <button
                                        data-testid={LANDING.formBtnSubmit}
                                        type="submit"
                                        disabled={submitting}
                                        className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {submitting ? 'Enviando…' : 'Aplicar a la misión'}
                                        {!submitting && (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </motion.form>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
};

export default LeadForm;
